/* =====================================================================
   wf-heic.js — iPhone HEIC photos -> JPEG before the theme's photo tools see them
   ---------------------------------------------------------------------
   Chrome, Edge and Firefox can't decode HEIC, so picking an iPhone photo
   (e.g. synced from iCloud to a PC) opened the Pintura "Edit your photo"
   editor on an empty canvas. iOS Safari already hands the page a JPEG and
   desktop Safari decodes HEIC itself, so only the other browsers need help.

   A capture-phase `change` listener on document runs before the theme's own
   handlers (jQuery, bound on each input). When a HEIC/HEIF file is picked and
   the browser can't decode it, the event is held, the file goes to the same
   Cloudinary account + unsigned preset the theme's uploader already uses, the
   JPEG rendition comes back, it is put into the input and `change` is fired
   again for the theme. (Client-side heic2any was tried first: it can't parse
   current iPhone HEICs.) If conversion fails the original file is passed
   through unchanged and the shopper is told to pick a JPG or PNG.
   ===================================================================== */
(function () {
  'use strict';

  var CLOUD = 'dgwldii6i';
  var PRESET = 'ml_unsigned';
  var JPEG_TRANSFORM = 'f_jpg,q_90,c_limit,w_3000,h_3000';   // Pintura output is 1000px; 3000 leaves crop room
  var TIMEOUT_MS = 45000;

  function isHeic(f) {
    return /hei[cf]/i.test(f.type || '') || /\.hei[cf]$/i.test(f.name || '');
  }

  // Safari decodes HEIC natively; only convert where decoding fails
  function canDecode(f) {
    if (!window.createImageBitmap) return Promise.resolve(false);
    return window.createImageBitmap(f).then(function (bmp) {
      if (bmp && bmp.close) bmp.close();
      return true;
    }, function () { return false; });
  }

  function withTimeout(p) {
    return Promise.race([p, new Promise(function (_, reject) {
      setTimeout(function () { reject(new Error('HEIC conversion timed out')); }, TIMEOUT_MS);
    })]);
  }

  function viaCloudinary(f) {
    var fd = new FormData();
    fd.append('file', f);
    fd.append('upload_preset', PRESET);
    return fetch('https://api.cloudinary.com/v1_1/' + CLOUD + '/image/upload', { method: 'POST', body: fd })
      .then(function (r) { if (!r.ok) throw new Error('upload ' + r.status); return r.json(); })
      .then(function (d) {
        if (!d || !d.public_id) throw new Error('no public_id');
        var url = 'https://res.cloudinary.com/' + CLOUD + '/image/upload/' + JPEG_TRANSFORM + '/' +
          (d.version ? 'v' + d.version + '/' : '') + d.public_id + '.jpg';
        return fetch(url);
      })
      .then(function (r) { if (!r.ok) throw new Error('jpeg ' + r.status); return r.blob(); })
      .then(function (blob) {
        var name = (f.name || 'photo').replace(/\.hei[cf]$/i, '') + '.jpg';
        return new File([blob], name, { type: 'image/jpeg', lastModified: f.lastModified || Date.now() });
      });
  }

  function toJpeg(f) {
    return canDecode(f).then(function (ok) { return ok ? f : withTimeout(viaCloudinary(f)); });
  }

  // one shared polite status line for screen readers + a small visible toast
  var statusEl = null, hideTimer = null;
  function status(msg, sticky) {
    if (!statusEl) {
      statusEl = document.createElement('div');
      statusEl.className = 'wf-heic-status';
      statusEl.setAttribute('role', 'status');
      statusEl.setAttribute('aria-live', 'polite');
      statusEl.style.cssText = 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:2147483000;' +
        'background:#1c1917;color:#fff;font-size:14px;line-height:1.35;padding:10px 16px;border-radius:8px;' +
        'box-shadow:0 6px 20px rgba(0,0,0,.25);max-width:calc(100vw - 32px);text-align:center;display:none';
      document.body.appendChild(statusEl);
    }
    clearTimeout(hideTimer);
    statusEl.textContent = msg || '';
    statusEl.style.display = msg ? 'block' : 'none';
    if (msg && !sticky) hideTimer = setTimeout(function () { status(''); }, 6000);
  }

  function refire(input, files) {
    try {
      var dt = new DataTransfer();
      files.forEach(function (f) { dt.items.add(f); });
      input.files = dt.files;
    } catch (e) { /* keep whatever the input holds */ }
    input._wfHeicPass = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input._wfHeicPass = false;
  }

  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || input.tagName !== 'INPUT' || input.type !== 'file' || input._wfHeicPass) return;
    if (input.closest && input.closest('.filepond--root')) return;          // FilePond reads its own files
    var files = input.files ? [].slice.call(input.files) : [];
    if (!files.length || !files.some(isHeic) || typeof DataTransfer === 'undefined') return;

    e.stopImmediatePropagation();                                           // hold the theme's handlers
    status('Preparing your photo…', true);
    Promise.all(files.map(function (f) { return isHeic(f) ? toJpeg(f) : f; })).then(function (out) {
      status('');
      refire(input, out);
    }, function () {
      status("We couldn't read that photo. Please choose a JPG or PNG.");
      refire(input, files);
    });
  }, true);
})();
